import api from "../api/config";
import { ENDPOINTS } from "../api/endpoints";

/**
 * Custom error class for "User not found" errors
 * Used to distinguish between user not found and other API errors
 */
export class UserNotFoundError extends Error {
  constructor(message) {
    super(message);
    this.name = "UserNotFoundError";
  }
}

/**
 * Fetch user information by username.
 * @param {string} user - Username or email to fetch information for.
 * @returns {Promise<Object>} Returns user info object.
 * @throws {UserNotFoundError} Throws UserNotFoundError if user is not found in the database.
 */
export const getUserInfo = async (user) => {
  if (!user) {
    throw new UserNotFoundError("User email is required");
  }

  try {
    const { data } = await api.post(ENDPOINTS.GET_USER_INFO, { user });
    if (!data) {
      throw new UserNotFoundError("User not found");
    }
    return data;
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error?.message || "";
    const statusCode = error?.response?.status;

    // Check if the error is a "User not found" error from the API
    if (statusCode === 404 || errorMessage.toLowerCase().includes("user not found")) {
      throw new UserNotFoundError("User not found");
    }
    
    console.error("Failed to fetch user information", error);
    throw error;
  }
};

/**
 * Fetches users for a specific company
 * New API endpoint that fetches company-based users
 * @param {string} companyName - Company name
 * @returns {Promise<Array>} Array of user objects with id, name, email, company_id
 */
export const getUsersByCompany = async (companyName) => {
  if (!companyName) {
    return [];
  }

  try {
    const url = ENDPOINTS.GET_USERS_BY_COMPANY(companyName);
    const response = await api.get(url);

    if (!response || !response.data || !response.data.success) {
      return [];
    }

    return response.data.users || [];
  } catch (error) {
    console.error('Error fetching users by company:', error);
    return [];
  }
};

/**
 * Fetches users with Sales Agent role for a specific company
 * Filters users by Sales Agent role (role_id: 4) on client side
 * @param {string} companyName - Company name
 * @returns {Promise<Array>} Array of user objects with id, name, email, company_id
 */
export const getCompanySalesReps = async (companyName) => {
  if (!companyName) {
    return [];
  }

  try {
    // Get all users for company and filter by Sales Agent role (role_id: 4)
    const allUsers = await getUsersByCompany(companyName);
    return allUsers;
  } catch (error) {
    console.error('Error fetching company sales reps:', error);
    return [];
  }
};

/**
 * Fetch all users (Admin-only).
 * Supports filtering, pagination, and search.
 * @param {Object} [filters={}] - Optional filter parameters.
 * @param {string} [filters.company] - Filter by company name.
 * @param {string|number|number[]} [filters.role_id] - Filter by role or roles.
 * @param {boolean} [filters.is_active] - Filter by active status.
 * @param {string} [filters.search] - Search keyword.
 * @param {number} [filters.page] - Current page number.
 * @param {number} [filters.limit] - Items per page.
 * @param {number} [filters.exclude_user_id] - User ID to exclude from results (e.g., current logged-in user).
 * @param {string} [filters.exclude_email] - User email to exclude from results (e.g., current logged-in user).
 * @returns {Promise<{success: boolean, users: Object[], count: number} | null>}
 */
export const getAllUsers = async (filters = {}) => {
  try {
    // Simulate small delay for smoother UX in loading states
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const params = new URLSearchParams();

    if (filters.company) params.append("company", filters.company);
    if (filters.search) params.append("search", filters.search);
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit);

    // Exclude current user from results
    if (filters.exclude_user_id) {
      params.append("exclude_user_id", filters.exclude_user_id);
    }
    if (filters.exclude_email) {
      params.append("exclude_email", filters.exclude_email);
    }

    // Handle role filters (single or multiple)
    if (filters.role_id) {
      const roles = Array.isArray(filters.role_id)
        ? filters.role_id
        : [filters.role_id];
      roles.forEach((id) => params.append("role_id[]", id));
    }

    if (filters.is_active !== undefined)
      params.append("is_active", filters.is_active);

    if (filters.sortOrder !== undefined && filters.sortOrder !== null) {
      params.append("sortOrder", filters.sortOrder);
    }

    if (filters.current_user_id) {
      params.append("current_user_id", filters.current_user_id);
    }
    if (filters.current_user_role_id) {
      params.append("current_user_role_id", filters.current_user_role_id);
    }
    if (filters.current_user_company) {
      params.append("current_user_company", filters.current_user_company);
    }

    const { data } = await api.get(
      `${ENDPOINTS.GET_ALL_USERS}?${params.toString()}`
    );

    if (!data?.success) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error fetching users", error);
    return null;
  }
};

/**
 * Fetch filter options for the user management table (e.g., company list, roles).
 * @returns {Promise<Object|null>} Returns filter options or null on failure.
 */
export const getFilterOptions = async () => {
  try {
    const { data } = await api.get(ENDPOINTS.GET_FILTER_OPTIONS);
    if (!data?.success) {
      console.error("Failed to fetch filter options");
      return null;
    }
    return data.filters;
  } catch (error) {
    console.error("Error fetching filter options", error);
    return null;
  }
};

/**
 * Create a new user (Admin-only).
 * Optionally sends a password reset email or returns a temporary password.
 * @param {Object} userData - User details.
 * @param {string} userData.name - User's name.
 * @param {string} userData.email - User's email.
 * @param {string} userData.company - User's company.
 * @param {number} userData.role_id - User's role ID.
 * @param {boolean} [userData.sendResetEmail=true] - Whether to send password setup email.
 * @returns {Promise<Object|null>} Returns created user or null on error.
 */
export const createUser = async (userData) => {
  try {
    const { name, email, company, role_id, sendResetEmail = true, user_id } = userData;

    // Basic validation
    if (!name || !email || !company || !role_id) {
      return { success: false, message: "All fields are required" };
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { success: false, message: "Please enter a valid email address" };
    }

    if (typeof role_id !== "number" || role_id < 1) {
      return { success: false, message: "Please select a valid user role" };
    }

    const payload = { name, email, company, role_id, sendResetEmail };
    // Add user_id if provided (for tracking created_by)
    if (user_id) {
      payload.user_id = user_id;
    }
    const { data } = await api.post(ENDPOINTS.CREATE_USER, payload);

    if (!data?.success) {
      return { success: false, message: data?.message || "Failed to create user", ...data };
    }

    return data;
  } catch (error) {
    console.error("Failed to create user", error);
    return { success: false, message: error?.response?.data?.message || "Failed to create user" };
  }
};

/**
 * Update an existing user's data.
 * @param {number} id - User ID.
 * @param {Object} updateData - Fields to update.
 * @returns {Promise<Object|null>} Returns updated user or null on failure.
 */
export const updateUser = async (id, updateData) => {
  try {
    // Ensure user_id is included in updateData if provided
    const payload = { ...updateData };
    if (updateData.user_id) {
      payload.user_id = updateData.user_id;
    }
    
    const { data } = await api.put(ENDPOINTS.UPDATE_USER(id), payload);
    if (!data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Failed to update user", error);
    return null;
  }
};

/**
 * Update user active/inactive status using email.
 * @param {string} email - User's email address.
 * @param {boolean} isActive - New status.
 * @param {number} userId - Current user ID (optional)
 * @param {number} roleId - Current user role ID (optional, for Super Admin check)
 * @returns {Promise<Object|null>} Returns updated user or null on failure.
 */
export const updateUserStatusByEmail = async (email, isActive, userId = null, roleId = null) => {
  try {
    const payload = {
      email,
      is_active: isActive,
    };
    // Add user_id if provided (for tracking updated_by)
    if (userId) {
      payload.user_id = userId;
    }
    // Add role_id if provided (for Super Admin check)
    if (roleId) {
      payload.current_user_role_id = roleId;
    }
    
    const { data } = await api.put(ENDPOINTS.UPDATE_USER_STATUS, payload);
    return data || null;
  } catch (error) {
    console.error("Failed to update user status", error);
    return null;
  }
};

/**
 * Update user role by email.
 * @param {string} email - User's email address.
 * @param {number} role_id - New role ID.
 * @param {number} userId - Current user ID (optional)
 * @param {number} roleId - Current user role ID (optional, for Super Admin check)
 * @returns {Promise<Object|null>} Returns updated user or null on failure.
 */
export const updateUserRoleByEmail = async (email, role_id, userId = null, roleId = null) => {
  try {
    const payload = {
      email,
      role_id,
    };
    // Add user_id if provided (for tracking updated_by)
    if (userId) {
      payload.user_id = userId;
    }
    // Add role_id if provided (for Super Admin check)
    if (roleId) {
      payload.current_user_role_id = roleId;
    }
    
    const { data } = await api.put(ENDPOINTS.UPDATE_USER_ROLE, payload);
    return data || null;
  } catch (error) {
    console.error("Failed to update user role", error);
    return null;
  }
};

/**
 * Permanently delete a user from Auth0 and the database.
 * @param {number} id - User ID.
 * @returns {Promise<boolean>} Returns true on full or partial success, false otherwise.
 */
export const deleteUser = async (id, userId = null) => {
  try {
    const config = userId ? { data: { user_id: userId } } : {};
    const { data } = await api.delete(ENDPOINTS.DELETE_USER(id), config);

    if (!data?.success) {
      return false;
    }

    if (data.deleted_from_auth0 && data.deleted_from_database) {
      return true;
    } else if (data.deleted_from_database) {
      return { success: true, warning: "User removed from database, but Auth0 cleanup incomplete. Please retry." };
    } else {
      return false;
    }
  } catch (error) {
    console.error("Failed to delete user", error);
    return false;
  }
};

/**
 * Reset a user's password by email (Admin action).
 * Allows manually setting a specific password.
 * @param {string} email - User's email.
 * @param {string} newPassword - New password to assign.
 * @returns {Promise<boolean>} Returns true if reset succeeded, false otherwise.
 */
export const resetUserPassword = async (email, newPassword, userId = null) => {
  try {
    const payload = {
      email,
      password: newPassword,
    };

    if (userId) {
      payload.user_id = userId;
    }

    const { data } = await api.put(ENDPOINTS.RESET_USER_PASSWORD, {
      ...payload,
    });

    if (!data?.success) {
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to reset password", error);
    return false;
  }
};

/** Export all API methods together for cleaner imports */
export default {
  getUserInfo,
  getAllUsers,
  getFilterOptions,
  createUser,
  updateUser,
  updateUserStatusByEmail,
  updateUserRoleByEmail,
  resetUserPassword,
  deleteUser,
};
